import { useAuthStoreShallow } from "@features/authentication";
import { cn } from "@lib/cn";
import { useIsCreatedByMeView } from "@pages/stories/hooks/use-is-created-by-me-view";
import { useStoriesStoreShallow } from "@pages/stories/store";
import { IconStar, IconUserCircle } from "@tabler/icons-react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

const MENU_ITEMS = [
  {
    label: "Created by me",
    icon: IconUserCircle,
    action: "created-by-me",
  },
  {
    label: "Favorites",
    icon: IconStar,
    action: "favorites",
  },
];

export const SidebarStoriesMenu = () => {
  const navigate = useNavigate();
  const { isFavoritesView, setIsFavoritesView, setOwner, setFolderId } =
    useStoriesStoreShallow((s) => ({
      isFavoritesView: s.isFavoritesView,
      setIsFavoritesView: s.setIsFavoritesView,
      setOwner: s.setOwner,
      setFolderId: s.setFolderId,
    }));
  const currentUserId = useAuthStoreShallow((s) => s.user?.uid ?? "");
  const isCreatedByMeView = useIsCreatedByMeView();

  const handleClick = useCallback(
    (action: string) => {
      if (action === "favorites") {
        navigate("/pages/editor");
        setIsFavoritesView(true);
        return;
      }
      // "Created by me" is the Owner filter set to you. The page then treats
      // it as a place of its own — own breadcrumb, no folders, no Owner pill —
      // see isCreatedByMeView for the one definition all of that reads.
      if (action === "created-by-me") {
        // setFolderId("") already routes back to /pages/editor, so the filter
        // applies to the whole list rather than the folder that was open.
        setFolderId("");
        setOwner(currentUserId);
      }
    },
    [navigate, setIsFavoritesView, setOwner, setFolderId, currentUserId],
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-1 tw-py-2">
      {MENU_ITEMS.map(({ label, icon: Icon, action }) => {
        const isActive =
          (action === "favorites" && isFavoritesView) ||
          (action === "created-by-me" && isCreatedByMeView);
        // The active row is drawn like a place of its own: the same fill the
        // folders and top-level items get (node 53:64173). The purple edge bar
        // that goes with it is rendered by SidebarBaseMenu — this list sits
        // inside the collapsible's `overflow-hidden`, which would clip it.
        return (
          <button
            onClick={() => handleClick(action)}
            type="button"
            key={label}
            className={cn(
              "tw-flex tw-h-8 tw-w-full tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-lg tw-py-2 tw-pl-8 tw-pr-2 tw-text-sm tw-text-blue tw-outline-none tw-transition-colors tw-duration-200 hover:tw-bg-blue-100/50",
              // blue-100 is #E7E6EC, the design's `--secondary` — which is
              // not a Tailwind colour here, so asking for `secondary` never
              // painted anything.
              isActive && "tw-bg-blue-100 hover:tw-bg-blue-100",
            )}
          >
            <Icon
              className={cn(
                "tw-size-4 tw-text-blue",
                !isActive && "tw-opacity-50",
              )}
            />
            <span className={cn(!isActive && "tw-opacity-50")}>{label}</span>
          </button>
        );
      })}
    </div>
  );
};
